import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, MapPin, Train, Clock } from 'lucide-react';
import { shuffleMix } from '../data/photos';

// Photo principale identique au site original - bols aériens vert/jaune
const BG_PHOTO = "/hero-bg.jpg";

const METRO_COLORS = {
  "3":  { bg: "#837902", text: "#fff" },
  "8":  { bg: "#D282BE", text: "#fff" },
  "9":  { bg: "#CEBD30", text: "#fff" },
  "12": { bg: "#007852", text: "#fff" },
  "14": { bg: "#62259D", text: "#fff" },
  "A":  { bg: "#E3051B", text: "#fff", rer: true },
};

const RESTAURANTS = [
  {
    id: "mogador",
    name: "Grainette",
    type: "Salades",
    badge: null,
    address: "27 rue de Mogador, 75009 Paris",
    hours: "Lun – Ven · 11h30 – 15h00",
    stations: [
      { name: "Trinité – d'Estienne d'Orves", lines: ["12"] },
    ],
    color: "#80a890",
    href: "https://clicks.tastycloud.fr/f8def329f79cc069cc86029fd12781cb49a501fadf230be5f094e9aeaf656e66/menu/73742",
    mapsHref: "https://maps.google.com/maps?q=27+rue+de+Mogador+75009+Paris",
  },
  {
    id: "godot-salad",
    name: "Grainette",
    type: "Salades",
    badge: null,
    address: "16 rue Godot de Mauroy, 75009 Paris",
    hours: "Lun – Ven · 11h30 – 15h00",
    stations: [
      { name: "Havre – Caumartin", lines: ["3", "9"] },
      { name: "Madeleine", lines: ["8", "12", "14"] },
      { name: "Auber", lines: ["A"], rer: true },
    ],
    color: "#80a890",
    href: "https://commandes.grainette.fr/home/1064/2/menu",
    mapsHref: "https://maps.google.com/maps?q=16+rue+Godot+de+Mauroy+75009+Paris",
  },
  {
    id: "godot-pates",
    name: "Grainette",
    type: "Pâtes",
    badge: null,
    address: "17 rue Godot de Mauroy, 75009 Paris",
    hours: "Lun – Ven · 11h30 – 14h30",
    stations: [
      { name: "Havre – Caumartin", lines: ["3", "9"] },
      { name: "Madeleine", lines: ["8", "12", "14"] },
      { name: "Auber", lines: ["A"], rer: true },
    ],
    color: "#EEB52D",
    href: "https://commandes.grainette.fr/home/4404/2/menu",
    mapsHref: "https://maps.google.com/maps?q=17+rue+Godot+de+Mauroy+75009+Paris",
  },
];

export default function Home() {
  const [showDropdown, setShowDropdown] = useState(false);
  const gallery = useMemo(() => shuffleMix(6), []);

  return (
    <div data-testid="home-page" className="relative">
      {/* ====== HERO FULLSCREEN ====== */}
      <section
        className="relative flex items-center justify-center"
        style={{ height: '100vh', minHeight: '600px' }}
        data-testid="hero-section"
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BG_PHOTO})` }}
        />
        {/* Subtle dark overlay for readability */}
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.08)' }} />

        {/* Center content */}
        <div className="relative z-10 flex flex-col items-center text-center px-4">
          {/* Logo */}
          <div
            className="mb-8 animate-fade-in"
            style={{ animationDelay: '0.1s' }}
          >
            <div style={{
              background: 'rgba(255,255,255,0.82)',
              borderRadius: '50%',
              width: 'min(55vw, 600px)',
              height: 'min(55vw, 600px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              padding: '24px',
              position: 'relative',
              overflow: 'visible',
            }}>
              <img
                src="/logo-hero.png"
                alt="Grainette"
                data-testid="hero-logo"
                style={{
                  width: 'min(70vw, 760px)',
                  height: 'min(70vw, 760px)',
                  objectFit: 'contain',
                  mixBlendMode: 'multiply',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              />
            </div>
          </div>

          {/* Commander button + dropdown */}
          <div className="relative animate-fade-in" style={{ animationDelay: '0.25s' }}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              data-testid="commander-hero-btn"
              className="flex items-center gap-2 px-10 py-4 rounded-full shadow-lg font-medium transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              style={{
                backgroundColor: '#80a890',
                color: 'white',
                fontFamily: '"Barlow", sans-serif',
                fontSize: '1rem',
                fontWeight: '800',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                minWidth: '200px',
                justifyContent: 'center',
              }}
            >
              Commander
              <ChevronDown
                size={20}
                className="transition-transform duration-300"
                style={{
                  transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  marginTop: '2px',
                }}
              />
            </button>

            {/* Dropdown restaurants enrichi */}
            {showDropdown && (
              <div
                data-testid="restaurant-dropdown"
                className="absolute left-1/2 mt-3 bg-white rounded-2xl shadow-2xl overflow-hidden z-50"
                style={{ transform: 'translateX(-50%)', width: '380px' }}
              >
                {/* Header */}
                <div className="px-5 py-3 border-b border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Choisir un restaurant</p>
                </div>

                {RESTAURANTS.map((r, i) => (
                  <div
                    key={r.id}
                    data-testid={`restaurant-card-${r.id}`}
                    className="px-5 py-4 hover:bg-gray-50 transition-colors"
                    style={{ borderBottom: i < RESTAURANTS.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                  >
                    {/* Nom + badge */}
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.color }} />
                      <span className="font-bold text-gray-900 text-sm">
                        {r.name} — <span style={{ color: r.color }}>{r.type}</span>
                      </span>
                      {r.badge && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: r.color }}>
                          {r.badge}
                        </span>
                      )}
                    </div>

                    {/* Adresse */}
                    <div className="flex items-start gap-2 mb-2 pl-4">
                      <MapPin size={11} className="shrink-0 mt-0.5" style={{ color: r.color }} />
                      <span className="text-xs text-gray-500">{r.address}</span>
                    </div>

                    {/* Stations */}
                    <div className="pl-4 space-y-1.5 mb-3">
                      {r.stations.map((station) => (
                        <div key={station.name} className="flex items-center gap-2">
                          <Train size={11} className="shrink-0 text-gray-400" />
                          <span className="text-xs text-gray-500 min-w-0">{station.name}</span>
                          <span className="flex gap-1 shrink-0">
                            {station.lines.map(l => {
                              const c = METRO_COLORS[l] || { bg: '#555', text: '#fff' };
                              return (
                                <span
                                  key={l}
                                  className="inline-flex items-center justify-center font-bold text-[9px]"
                                  style={{
                                    backgroundColor: c.bg,
                                    color: c.text,
                                    width: c.rer ? '18px' : '16px',
                                    height: '16px',
                                    borderRadius: c.rer ? '3px' : '50%',
                                    lineHeight: 1,
                                  }}
                                >
                                  {c.rer ? `RER` : l}
                                </span>
                              );
                            })}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pl-4">
                      <a
                        href={r.mapsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`maps-link-${r.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors hover:bg-gray-100"
                        style={{ borderColor: r.color, color: r.color }}
                      >
                        <MapPin size={11} /> Itinéraire
                      </a>
                      <a
                        href={r.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`order-link-${r.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90"
                        style={{ backgroundColor: r.color }}
                        onClick={() => setShowDropdown(false)}
                      >
                        Commander <ArrowRight size={11} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Click outside to close */}
          {showDropdown && (
            <div
              className="fixed inset-0"
              style={{ zIndex: -1 }}
              onClick={() => setShowDropdown(false)}
            />
          )}
        </div>
      </section>

      {/* ====== SECTION CONCEPT ====== */}
      <section className="py-20 px-6 bg-[#F5F5F5]" data-testid="concept-section">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl overflow-hidden px-8 py-10" style={{ backgroundColor: '#4a6b5a' }}>

            {/* Logo centré */}
            <div className="text-center mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_grainette-migration/artifacts/i2pxliea__Grainette%20_%20.png"
                alt="Grainette"
                className="w-auto object-contain mx-auto"
                style={{ height: '11rem', filter: 'brightness(0) invert(1)' }}
              />
            </div>

            {/* Intro */}
            <p className="text-center text-sm leading-relaxed max-w-xl mx-auto mb-10" style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.90)' }}>
              De la sélection des ingrédients jusqu'à la remise du sac, chaque étape est guidée par l'envie de bien faire — pour une expérience simple, fluide et de qualité.
            </p>

            {/* 3 piliers */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                {
                  title: "Simple & Rapide",
                  text: "Un concept intuitif, un service chaleureux et cadencé pour que l'attente ne soit jamais un frein. L'efficacité de la restauration moderne, sans sacrifier la qualité.",
                },
                {
                  title: "Sur Mesure",
                  text: "Plus de 80 produits sélectionnés pour composer le plat qui vous ressemble — librement, selon vos saveurs, vos textures et vos envies du moment.",
                },
                {
                  title: "Expérience Client",
                  text: "Une atmosphère chaleureuse et conviviale, pensée pour offrir une vraie coupure au cœur de la journée de travail.",
                },
              ].map((p) => (
                <div key={p.title} className="flex flex-col gap-3">
                  <div className="w-8 h-0.5" style={{ backgroundColor: '#80a890' }} />
                  <h3 className="text-xl font-black text-white" style={{ fontFamily: 'Barlow, sans-serif' }}>
                    {p.title}
                  </h3>
                  <p className="text-sm leading-relaxed font-medium" style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.88)' }}>
                    {p.text}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ====== GALERIE PHOTOS ====== */}
      <section className="pb-20 px-6 bg-[#F5F5F5]" data-testid="gallery-section">
        <div className="max-w-6xl mx-auto">
          <div className="gallery-masonry">
            {gallery.map((photo, i) => (
              <div key={i} className="gallery-masonry-item">
                <img src={photo.src} alt="Grainette" loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== NOTRE HISTOIRE ====== */}
      <section className="py-20 px-6 bg-[#F5F5F5]" data-testid="notre-histoire-section">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">

            {/* Texte */}
            <div className="flex-1">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-4" style={{ color: '#80a890' }}>
                Notre histoire
              </p>
              <h2
                className="text-4xl sm:text-5xl font-black mb-6"
                style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}
              >
                Justine &amp; Geoffrey
              </h2>
              <div className="space-y-4 text-gray-600 text-base leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <p>
                  Plus qu'un couple, une équipe. Forts de plus de 20 ans de métier — entre restauration collective et pâtisserie — nous avons ouvert notre premier bar à salade en 2012.
                </p>
                <p>
                  Après sept années d'apprentissage, nous avons tout repensé pour créer une expérience plus authentique et innovante. <strong style={{ color: '#2A3630' }}>Grainette était née.</strong>
                </p>
                <p>
                  Aujourd'hui, nous gérons nos trois adresses à taille humaine, dans un esprit résolument familial — parce que c'est comme ça qu'on fait les meilleures choses.
                </p>
              </div>
            </div>

            {/* Photo */}
            <div className="flex-shrink-0 w-full lg:w-auto">
              <div
                className="overflow-hidden rounded-2xl shadow-md"
                style={{ width: '320px', maxWidth: '100%', margin: '0 auto' }}
              >
                <img
                  src="/photos/team/justine-geoffrey.jpg"
                  alt="Justine et Geoffrey — fondateurs de Grainette"
                  className="w-full h-auto block"
                  loading="lazy"
                  decoding="async"
                  data-testid="team-photo"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ====== NOS ADRESSES ====== */}
      <section className="py-20 px-6 bg-[#F5F5F5]" data-testid="locations-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: '#80a890' }}>
              Paris 9e
            </p>
            <h2
              className="text-4xl sm:text-5xl font-black"
              style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}
            >
              Nos Adresses
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {RESTAURANTS.map((r) => (
              <div
                key={r.id}
                data-testid={`location-card-${r.id}`}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex flex-col"
              >
                {/* Barre couleur */}
                <div className="h-1.5 w-full" style={{ backgroundColor: r.color }} />

                <div className="p-6 flex flex-col flex-1">
                  {/* Titre */}
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="font-black text-xl" style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}>
                      {r.name}
                    </h3>
                    <span className="text-sm font-semibold" style={{ color: r.color }}>— {r.type}</span>
                  </div>

                  {/* Détails */}
                  <div className="space-y-2.5 mb-5 flex-1">
                    <div className="flex items-start gap-2 text-gray-500 text-sm">
                      <MapPin size={14} className="shrink-0 mt-0.5" style={{ color: r.color }} />
                      <span>{r.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Clock size={14} className="shrink-0" style={{ color: r.color }} />
                      <span>{r.hours}</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-400 text-xs pt-1">
                      <Train size={13} className="shrink-0 mt-0.5" style={{ color: r.color }} />
                      <div className="flex flex-col gap-1">
                        {r.stations.map((s) => (
                          <div key={s.name} className="flex flex-wrap items-center gap-1">
                            <span>{s.name}</span>
                            {s.lines.map((l) => (
                              <span
                                key={l}
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                                style={{
                                  backgroundColor: METRO_COLORS[l]?.bg || '#999',
                                  color: METRO_COLORS[l]?.text || '#fff',
                                }}
                              >
                                {s.rer ? `RER ${l}` : l}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Boutons */}
                  <div className="flex gap-2 flex-wrap">
                    <a
                      href={r.mapsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`maps-card-${r.id}`}
                      className="px-4 py-2 text-sm font-semibold rounded-full border-2 btn-lift"
                      style={{ borderColor: r.color, color: r.color }}
                    >
                      Itinéraire
                    </a>
                    <a
                      href={r.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`order-card-${r.id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-white text-sm font-semibold rounded-full btn-lift"
                      style={{ backgroundColor: r.color }}
                    >
                      Commander <ArrowRight size={13} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== CTA RECRUTEMENT ====== */}
      <section
        className="py-20 px-4 text-white text-center"
        style={{ backgroundColor: '#2A3630' }}
        data-testid="cta-section"
      >
        <div className="max-w-2xl mx-auto">
          <h2
            className="text-4xl sm:text-5xl font-black mb-4"
            style={{ fontFamily: 'Barlow, sans-serif' }}
          >
            Rejoignez l'équipe !
          </h2>
          <p className="text-gray-300 mb-8 text-base">
            On recrute des équipiers dynamiques et souriants — horaires du midi, Paris 9e.
          </p>
          <Link
            to="/recrutement"
            data-testid="cta-recruitment-btn"
            className="inline-flex items-center gap-2 px-8 py-4 font-semibold rounded-full text-sm btn-lift"
            style={{ backgroundColor: '#80a890', color: 'white' }}
          >
            Postuler <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
